export type Theme = 'dark' | 'light' | 'system'

// Same key as dashboard (`replay_theme`) so a shared browser on a shared host would agree. The two
// apps are on different origins today, so this is a name, not a sync.
export const THEME_STORAGE_KEY = 'replay_theme'
export const DEFAULT_THEME: Theme = 'system'

export function isTheme(value: string | null | undefined): value is Theme {
  return value === 'dark' || value === 'light' || value === 'system'
}

export function getStoredTheme(): Theme {
  if (typeof window === 'undefined') return DEFAULT_THEME
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    return isTheme(stored) ? stored : DEFAULT_THEME
  } catch {
    return DEFAULT_THEME
  }
}

export function storeTheme(theme: Theme) {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme)
  } catch {
    // A full or blocked store just means the next visit falls back to system.
  }
}

export function getEffectiveTheme(theme: Theme, prefersDark?: boolean): 'dark' | 'light' {
  if (theme !== 'system') return theme
  if (typeof prefersDark === 'boolean') return prefersDark ? 'dark' : 'light'
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function applyThemeToDOM(effectiveTheme: 'dark' | 'light') {
  if (typeof document === 'undefined') return
  document.documentElement.setAttribute('data-theme', effectiveTheme)
  document.documentElement.classList.toggle('dark', effectiveTheme === 'dark')
}
